/**
 * Mapa central de capacidades por rol.
 *
 * ## Por que existe
 *
 * Antes, cada vista escribia su propia lista de roles permitidos para cada
 * accion:
 *
 *   const rolesAllowed = ["ROLE_ADMINISTRATOR_SUCURSAL", "ROLE_ADMIN"]
 *   return rolesAllowed.some(role => this.roles.includes(role));
 *
 * Eso duplicaba la definicion de "quien puede hacer que" en ~20 archivos, sin
 * ninguna forma de detectar si dos vistas discrepaban. Peor: las listas no
 * coincidian. `canCancel` en ListaVenta excluia a `ROLE_COBRANZA` mientras
 * `allowPayment` en ActionsProveedor lo incluia, sin que nadie supiera si
 * era intencionado.
 *
 * Aqui cada ACCION tiene una entrada, y las vistas preguntan por la accion:
 *
 *   v-if="can(CAP.VENTA_CANCELAR)"
 *
 * ## Los tres exports
 *
 * | Export | Que es | Ejemplo |
 * |---|---|---|
 * | `ROLES` | enum de los roles del sistema | `ROLES.ADMIN` -> `'ROLE_ADMIN'` |
 * | `CAP` | enum de los NOMBRES de las capacidades | `CAP.VENTA_CANCELAR` -> `'VENTA_CANCELAR'` |
 * | `CAPACIDADES` | mapa accion -> roles que la pueden ejecutar | `CAPACIDADES.VENTA_CANCELAR` -> `[...]` |
 *
 * Ojo: `CAPACIDADES.X` es la **lista de roles**, no el nombre de la
 * capacidad. `can()` espera el nombre, asi que se le pasa `CAP.X`.
 *
 * ## Regla de resolucion
 *
 * El permiso se concede si CUALQUIERA de los roles del usuario lo permite.
 * Un usuario con `['ROLE_USER', 'ROLE_ADMIN']` tiene los permisos de
 * `ROLE_ADMIN`.
 *
 * ## Nota sobre el menu y el router
 *
 * Este mapa gobierna los controles de ACCION dentro de las pantallas (botones,
 * campos editables, items de menu). El menu lateral (`Permisos.js`) y el
 * acceso por URL (`adminRouter.js`) siguen siendo listas por rol: un rol
 * distinto produce un menu distinto, y un usuario con varios roles no puede
 * tener dos menus a la vez. Esa limitacion es del contrato actual del
 * backend (un solo `roleName` por usuario), no del codigo.
 */

import { auth } from '@/store/auth.module';

/**
 * Los roles del sistema, en un solo lugar.
 *
 * El backend los expone como `roleName` (un string) en el perfil y como
 * array `roles` en el login.
 */
export const ROLES = {
    ADMIN: 'ROLE_ADMIN',
    VENDEDOR: 'ROLE_VENDEDOR',
    COBRANZA: 'ROLE_COBRANZA',
    ADMINISTRATOR_SUCURSAL: 'ROLE_ADMINISTRATOR_SUCURSAL',
    ADMINISTRATOR_PRECIO: 'ROLE_ADMINISTRATOR_PRECIO',
    USER: 'ROLE_USER',
};

/**
 * Los nombres de las capacidades.
 *
 * Este es el enum que consumen las vistas: `can(CAP.VENTA_CANCELAR)`.
 *
 * El valor de cada entrada es **igual a su propia clave**, por lo que
 * `can(CAP.VENTA_CANCELAR)` y `can('VENTA_CANCELAR')` son equivalentes. La
 * forma con enum existe para que el editor autocomplete y para que un typo
 * sea visible de inmediato (daria `undefined` en vez de un string
 * silenciosamente incorrecto).
 *
 * Ojo con la confusion habitual: `CAPACIDADES.VENTA_CREAR` NO es el nombre
 * de la capacidad, es la **lista de roles** que la pueden ejecutar. El
 * nombre es `CAP.VENTA_CREAR`.
 */
export const CAP = {
    ADMIN: 'ADMIN',
    SOLO_USUARIO: 'SOLO_USUARIO',
    VENTA_CREAR: 'VENTA_CREAR',
    VENTA_CANCELAR: 'VENTA_CANCELAR',
    VENTA_PRECIO_EDITAR: 'VENTA_PRECIO_EDITAR',
    REPORTE_COBRANZA_VER: 'REPORTE_COBRANZA_VER',
    DASHBOARD_TODAS_SUCURSALES_VER: 'DASHBOARD_TODAS_SUCURSALES_VER',
    DASHBOARD_RESUMEN_VER: 'DASHBOARD_RESUMEN_VER',
    DASHBOARD_VENTAS_VER: 'DASHBOARD_VENTAS_VER',
    DASHBOARD_TOP_PRODUCTOS_VER: 'DASHBOARD_TOP_PRODUCTOS_VER',
    DASHBOARD_CUENTAS_VER: 'DASHBOARD_CUENTAS_VER',
    PAGO_PROVEEDOR_REGISTRAR: 'PAGO_PROVEEDOR_REGISTRAR',
    PAGO_ELIMINAR: 'PAGO_ELIMINAR',
    CLIENTE_ELIMINAR: 'CLIENTE_ELIMINAR',
    CLIENTE_VER_TODOS: 'CLIENTE_VER_TODOS',
    CAJA_OPERAR: 'CAJA_OPERAR',
    CAJA_REABRIR: 'CAJA_REABRIR',
    REPORTE_PRODUCTIVIDAD_VER: 'REPORTE_PRODUCTIVIDAD_VER',
    REPORTE_CREDITOS_VER: 'REPORTE_CREDITOS_VER',
    DEDUCCION_ABONO_CANCELAR: 'DEDUCCION_ABONO_CANCELAR',
    SUCURSAL_TODAS_VER: 'SUCURSAL_TODAS_VER',
};

/**
 * Capacidades: accion -> roles que la pueden ejecutar.
 *
 * Cada entrada es una accion concreta de la interfaz, no una pantalla.
 */
export const CAPACIDADES = {

    // --- Base ---
    // Administrador: puede hacer casi todo. Se usa para los controles que
    // antes eran `isAdmin` (`getUserRoles().includes('ROLE_ADMIN')`).
    ADMIN: [
        ROLES.ADMIN,
    ],
    // Usuario basico: en el Dashboard sus tarjetas no tienen enlace. El
    // criterio es "si ALGUN rol del usuario es ROLE_USER, no hay acceso".
    SOLO_USUARIO: [
        ROLES.USER,
    ],

    // --- Ventas ---
    // Crear una venta. Coincide con lo que ya hacia `canSell` en
    // ListaVenta: VENDEDOR, COBRANZA, ADMINISTRATOR_SUCURSAL o ADMIN.
    VENTA_CREAR: [
        ROLES.VENDEDOR,
        ROLES.COBRANZA,
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
    ],
    // Cancelar una venta. Coincide con `canCancel` en ListaVenta.
    VENTA_CANCELAR: [
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
    ],
    // Editar el precio de una venta (campo readonly en NuevaVenta).
    VENTA_PRECIO_EDITAR: [
        ROLES.ADMIN,
    ],

    // --- Cobranza y pagos ---
    // Ver el reporte de cobranza. Coincide con `canSeeReportCobranza` en
    // ListaCliente.
    REPORTE_COBRANZA_VER: [
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
        ROLES.COBRANZA,
    ],
    // Dashboard con datos de TODAS las sucursales: compras, cuentas por pagar y
    // por cobrar, top de clientes y comparativo por sucursal. Coincide con
    // `DashboardService.scope()` del backend (ADMIN y ADMINISTRATOR_PRECIO); el
    // resto de los roles ve el Dashboard solo con los datos de su sucursal y el
    // backend responde 403 a esas vistas.
    DASHBOARD_TODAS_SUCURSALES_VER: [
        ROLES.ADMINISTRATOR_PRECIO,
        ROLES.ADMIN,
    ],
    // Pestañas del Dashboard. Inventario no lleva capacidad: lo ven todos (cada quien con su sucursal).
    // Resumen de dinero: solo ADMIN y ADMINISTRATOR_PRECIO (todas las sucursales). Ventas y piezas también la ven
    // VENDEDOR, ADMINISTRATOR_SUCURSAL y COBRANZA, con los datos de su sucursal (el backend los fija a ella).
    DASHBOARD_RESUMEN_VER: [
        ROLES.ADMIN,
        ROLES.ADMINISTRATOR_PRECIO,
    ],
    DASHBOARD_VENTAS_VER: [
        ROLES.ADMIN,
        ROLES.ADMINISTRATOR_PRECIO,
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.VENDEDOR,
        ROLES.COBRANZA,
    ],
    // Ranking de productos (dentro de Ventas y piezas): solo los roles de todas las sucursales.
    DASHBOARD_TOP_PRODUCTOS_VER: [
        ROLES.ADMIN,
        ROLES.ADMINISTRATOR_PRECIO,
    ],
    // Compras y cuentas por cobrar/pagar. Coincide con `DashboardService.requireCompanyWide()` del backend
    // (todas las sucursales + COBRANZA, que las ve de todas las sucursales).
    DASHBOARD_CUENTAS_VER: [
        ROLES.ADMIN,
        ROLES.ADMINISTRATOR_PRECIO,
        ROLES.COBRANZA,
    ],
    // Registrar el pago a un proveedor. Coincide con `allowPayment` en
    // ActionsProveedor.
    PAGO_PROVEEDOR_REGISTRAR: [
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
        ROLES.COBRANZA,
    ],
    // Eliminar / restaurar un pago. Coincide con `isAdmin` en las listas de
    // pago (venta y proveedor).
    PAGO_ELIMINAR: [
        ROLES.ADMIN,
    ],

    // --- Clientes y proveedores ---
    // Eliminar / restaurar un cliente. Coincide con `isAdmin` en
    // ActionsCliente.
    CLIENTE_ELIMINAR: [
        ROLES.ADMIN,
    ],
    // Ver todos los clientes (no solo los de la sucursal). Coincide con
    // `canSeeAll` en los exportadores de venta.
    CLIENTE_VER_TODOS: [
        ROLES.COBRANZA,
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
    ],

    // --- Caja ---
    // Operar la caja (abrir/cerrar). Coincide con `isSeller` en CashRegister.
    CAJA_OPERAR: [
        ROLES.VENDEDOR,
        ROLES.COBRANZA,
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
    ],
    // Reabrir una caja cerrada. Coincide con `isAdmin` en ActionsCashRegister.
    CAJA_REABRIR: [
        ROLES.ADMIN,
    ],

    // --- Reportes ---
    // Ver el reporte de productividad. `reportsByRol` en ListaReporte lo
    // excluia si el PRIMER rol del usuario estaba en la lista de excluidos.
    REPORTE_PRODUCTIVIDAD_VER: [
        ROLES.VENDEDOR,
        ROLES.COBRANZA,
        ROLES.ADMIN,
    ],
    // Ver el reporte de creditos pendientes (id 4 de la lista).
    REPORTE_CREDITOS_VER: [
        ROLES.VENDEDOR,
        ROLES.COBRANZA,
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMINISTRATOR_PRECIO,
        ROLES.ADMIN,
    ],

    // --- Deducciones ---
    // Cancelar un abono. Coincide con `isAdmin` en las vistas de deduccion.
    DEDUCCION_ABONO_CANCELAR: [
        ROLES.ADMIN,
    ],

    // --- Sucursal ---
    // Elegir cualquier sucursal en los exportadores (no solo la propia).
    // Antes: `['ROLE_COBRANZA', 'ROLE_ADMINISTRATOR_SUCURSAL'] || isAdmin`.
    SUCURSAL_TODAS_VER: [
        ROLES.COBRANZA,
        ROLES.ADMINISTRATOR_SUCURSAL,
        ROLES.ADMIN,
    ],
};

/**
 * Comprueba si el usuario puede ejecutar una capacidad.
 *
 * @param {string} capacidad  nombre de la capacidad (p. ej. `CAP.VENTA_CANCELAR`).
 * @param {string[]} roles    roles del usuario. Si se omite, se leen del
 *                             usuario autenticado.
 * @returns {boolean} true si CUALQUIER rol del usuario permite la capacidad.
 *
 * @example
 *   v-if="can(CAP.VENTA_CANCELAR)"
 */
export const can = (capacidad, roles) => {
    const permitidos = CAPACIDADES[capacidad];

    // Una capacidad desconocida se deniega (fail-closed): si alguien escribe
    // 'VENTA_CANCLAR' por typo, el boton se oculta en vez de mostrarse a
    // cualquiera.
    if (!permitidos) {
        return false;
    }

    const rolesUsuario = roles ?? getUserRoles();
    if (!Array.isArray(rolesUsuario)) {
        return false;
    }

    return permitidos.some((rol) => rolesUsuario.includes(rol));
};

/**
 * Obtiene los roles del usuario autenticado.
 *
 * La fuente de verdad es el store de Vuex (`auth.state.user.roles`), que es
 * lo que usan las vistas. NO se lee de `localStorage` a proposito: el store se
 * actualiza en el login, mientras que `localStorage` puede quedar
 * desincronizado (por ejemplo tras un `validateToken` que no lo reescribe).
 *
 * No hay dependencia circular: `auth.module` solo importa `auth.service`, que
 * a su vez solo importa axios.
 *
 * @returns {string[]} los roles del usuario, o un array vacio.
 */
const getUserRoles = () => {
    try {
        return auth.state.user?.roles ?? [];
    } catch (error) {
        return [];
    }
};

export default CAPACIDADES;
